import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchNotifications } from './useFetchNotifications';

interface UseNotificationModalProps {
  initialTab?: 'all' | 'unread' | 'read';
  onClose: () => void;
}

export const useNotificationModal = ({
  initialTab = 'all',
  onClose,
}: UseNotificationModalProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);

  const {
    notifications,
    readCount,
    unreadCount,
    allCount,
    error,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    markNotification
  } = useFetchNotifications({filter: [activeTab]});

  const handleTabChange = useCallback((newValue: 'all' | 'unread' | 'read') => {
    setActiveTab(newValue);
  }, []);

  const handleNavigate = useCallback(() => {
    onClose();
    navigate('/notifications');
  }, [navigate, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter((n) => n.status === 'unread');
    }
    if (activeTab === 'read') {
      return notifications.filter((n) => n.status === 'read');
    }
    return notifications;
  }, [activeTab, notifications]);

  return {
    notifications: filteredNotifications,
    readCount,
    unreadCount,
    allCount,
    error,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    activeTab,
    setActiveTab: handleTabChange,
    handleNavigateToNotifications: handleNavigate,
    handleCloseModal: handleClose,
    markNotification
  };
};
